import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS, track } from "@/lib/site";
import { useSettings } from "@/hooks/useContent";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { data: settings } = useSettings();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logo = settings?.logo_url || LOGO_URL;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b bg-white/95 backdrop-blur transition-shadow ${scrolled ? "border-line shadow-[0_8px_24px_-18px_rgba(11,25,44,0.35)]" : "border-transparent"}`}
    >
      <div className="container-x flex h-[68px] items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" aria-label="Orvella Overseas home" data-testid="header-logo-link">
          <img src={logo} alt="Orvella Overseas Private Limited logo" className="h-11 w-auto max-w-[190px] object-contain object-left mix-blend-multiply" loading="eager" />
        </Link>

        <nav className="hidden items-center gap-5 xl:flex" aria-label="Main navigation">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative py-1 text-[13px] font-semibold tracking-wide transition-colors hover:text-gold-dark ${isActive ? "text-gold-dark" : "text-navy"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/quote"
            data-testid="header-request-quote-btn"
            className="btn-gold hidden !px-5 !py-2.5 !text-[12px] md:inline-flex"
          >
            Request a Quote
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line text-navy xl:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-line bg-white xl:hidden" data-testid="mobile-menu">
          <nav className="container-x grid gap-1 py-4" aria-label="Mobile navigation">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2.5 text-sm font-semibold ${isActive ? "bg-sand text-gold-dark" : "text-navy"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/quote" className="btn-gold mt-2" data-testid="mobile-menu-quote-btn">
              Request a Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function trackWhatsAppClick() {
  track("whatsapp_click");
}
